/* Phiếu tư vấn — hồ sơ khách, báo giá online · cọc của Telesales, tiến trình khách, ghi chép tư vấn tại cơ sở */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Modal, Select, Tag, Tooltip, message } from 'antd';
import dayjs from 'dayjs';
import { Check, Eye, EyeOff, Flag, Stethoscope } from 'lucide-react';
import { STEPS, branchName, curStep, custTitle, dmhm, doctorName, hm, stepDone, vnd, type Visit } from './logic';
import { useDoctors } from './receptionApi';
import { inviteDoctor } from './examApi';
import { useTuVan } from './store';
import { journeyOf } from './tele';
import { useLeadStore } from '../Telesales/lead/store';
import { Card, DoctorGate, FlowBar, Hint, NoVisit, NotStarted, Row, Shell, SourceTag, useMe, useVisit, who } from './shared';
import ScreeningView from './ScreeningView';

const TEMPLATE = 'Vấn đề khách gặp:\nGiải pháp / phác đồ đề xuất:\nKết quả khách mong muốn:\nKhách cần cân nhắc:';

function Body({ v }: { v: Visit }) {
  const navigate = useNavigate();
  const me = useMe();
  const update = useTuVan((s) => s.update);
  const showJourney = useTuVan((s) => s.showJourney);
  const setStore = useTuVan((s) => s.set);
  const [note, setNote] = useState(v.note);
  const [invite, setInvite] = useState(false);
  const doctors = useDoctors();
  const [picked, setDoctor] = useState<string>();
  const doctor = picked ?? v.invite?.doctor ?? doctors[0].id;
  const dirty = note !== v.note;
  const go = (url: string) => {
    if (dirty) update(v.id, () => ({ note }), { text: 'Lưu ghi chép tư vấn', by: me.name });
    navigate(`${url}?v=${v.id}`);
  };
  const save = () => {
    update(v.id, () => ({ note }), { text: 'Lưu ghi chép tư vấn', by: me.name });
    message.success('Đã lưu ghi chép.');
  };
  const sendInvite = async () => {
    const r = await inviteDoctor(v, doctor);
    if (!r.ok) { message.error(r.msg); return; }
    update(v.id, () => ({ invite: { doctor, at: Date.now() } }), { text: `Mời ${doctorName(doctor)} khám`, by: me.name });
    setInvite(false);
    message.success(`Đã mời ${doctorName(doctor)} vào ${v.room ?? 'phòng tư vấn'}.`);
    go('/bac-si-kham');
  };
  const expired = v.online && v.online.validUntil < Date.now();
  const lead = useLeadStore((s) => (v.leadId ? s.byId[v.leadId] : undefined));
  const journey = useMemo(() => journeyOf(v, lead), [v, lead]);
  const changes = journey.filter((j) => j.change).length;
  // Các bước của khách: trước khi đến (nếu có) → check-in → nhận tư vấn → 6 bước App Tư vấn
  const stages: { key: string; label: string; done: boolean; hint?: string; url?: string }[] = [
    ...(v.source === 'Telesales' || journey.length ? [{ key: 'tele', label: 'Telesales tư vấn', done: true, hint: v.owner }] : []),
    ...(v.apptAt ? [{ key: 'appt', label: 'Đặt lịch', done: true, hint: dmhm(v.apptAt) }] : []),
    { key: 'checkin', label: 'Check-in', done: !!v.checkInAt, hint: v.checkInAt ? hm(v.checkInAt) : undefined },
    { key: 'assign', label: 'Nhận tư vấn', done: !!v.consultant && v.status !== 'waiting', hint: v.consultant ? `${who(v.consultant)}${v.room ? ` · ${v.room}` : ''}` : undefined },
    ...STEPS.map((s) => ({ key: s.key, label: s.label, done: stepDone(v, s.key), url: s.url })),
  ];
  const cs = curStep(v);
  const curKey = !v.checkInAt ? 'checkin' : cs === 'wait' ? 'assign' : cs;
  const curIdx = stages.findIndex((s) => s.key === curKey);

  return (
    <>
      <NotStarted v={v} />
      {/* Máy tính: trái = hồ sơ, báo giá, khai báo, ghi chép · phải = tiến trình (dính khi cuộn) — không để khoảng trắng dưới các thẻ ngắn.
          Điện thoại: ô ghi chép (việc chính khi ngồi với khách) lên đầu */}
      <div className="grid grid-cols-1 items-start gap-3 sm:gap-4 lg:grid-cols-3">
      <div className="flex min-w-0 flex-col gap-3 sm:gap-4 lg:col-span-2">
      <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2">
        <Card title="Hồ sơ khách">
          <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-700/40">
            <div className="text-[15px] font-bold text-gray-800 dark:text-gray-100">{custTitle(v.cust)}</div>
            <div className="mt-0.5 text-[13px] text-gray-500">{v.cust.code} · {v.cust.phone} · {branchName(v.branchId)}</div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {v.checkInAt ? <Tag color="green" className="!m-0">Đã đến {hm(v.checkInAt)}</Tag> : <Tag className="!m-0">Chưa đến</Tag>}
              <SourceTag v={v} />
              {v.visitNo > 1 && <Tag color="blue" className="!m-0">Lần đến thứ {v.visitNo}</Tag>}
              {v.flags?.map((f) => <Tag key={f} color="red" className="!m-0">{f}</Tag>)}
            </div>
          </div>
          <div className="mt-3 space-y-2">
            <Row label="Mảng · nhu cầu"><b>{v.mang}</b> · {v.need}</Row>
            {v.pageStaff && <Row label="Trực page tạo">{v.pageStaff}</Row>}
            <Row label={v.source === 'Telesales' ? 'Telesales tư vấn' : 'Người phụ trách'}>{v.owner ?? <span className="text-gray-400">Chưa có (khách đến trực tiếp)</span>}</Row>
            <Row label="Tư vấn cơ sở">{v.consultant ? `${who(v.consultant)}${v.room ? ` · ${v.room}` : ''}` : <span className="text-gray-400">Chưa phân</span>}</Row>
            {v.referral && <Row label="Phiếu CSKH">{v.referral.note}</Row>}
          </div>
        </Card>

        <Card title="Báo giá online · cọc online">
          {v.online ? (
            <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-700/40">
              <div className="flex flex-wrap items-center justify-between gap-1 font-semibold text-gray-800 dark:text-gray-100">
                {v.online.code}
                <Tag color={expired ? 'red' : 'blue'} className="!m-0">{expired ? 'Hết hạn' : `Hạn ${dayjs(v.online.validUntil).format('DD/MM')}`}</Tag>
              </div>
              <div className="mt-1 text-xs text-gray-500">Gửi {dmhm(v.online.sentAt)} · {v.online.by}</div>
              <ul className="mt-2 space-y-1 text-[13px]">
                {v.online.items.map((x) => <li key={x.name} className="flex justify-between gap-2"><span>{x.name}</span><span className="whitespace-nowrap">{vnd(x.price)}</span></li>)}
              </ul>
              <div className="mt-2 border-t border-gray-200 pt-2 text-[13px] dark:border-gray-600">Giá từ <b className="text-[#3e91ab]">{vnd(v.online.total)}</b> <span className="text-xs text-gray-500">— giá tham khảo, báo giá chính thức sau khám</span></div>
            </div>
          ) : <Hint>Không có báo giá online.</Hint>}
          <div className="mt-3 space-y-2">
            {(v.onlineDeps ?? []).map((d) => (
              <div key={d.code} className="flex items-center justify-between gap-2 rounded-lg border border-gray-100 px-3 py-2 text-[13px] dark:border-gray-700">
                <span>Cọc {d.code} · {d.purpose}</span>
                <span className={d.received ? 'font-semibold text-green-600 dark:text-green-400' : 'text-amber-600'}>{vnd(d.amount)} · {d.status}</span>
              </div>
            ))}
            {!v.onlineDeps?.length && v.online && <Hint>Khách chưa cọc online.</Hint>}
          </div>
          <Hint>Mức trợ giá Telesales đã dùng không hiện với Tư vấn cơ sở. Cọc online đã nhận sẽ trừ ở POS khi thanh toán.</Hint>
        </Card>
      </div>

      <ScreeningView v={v} />

      <Card className="order-first lg:order-none" title="Ghi chép tư vấn tại cơ sở (sàng lọc)" extra={v.invite && <Tag color="cyan" className="!m-0">Đã mời {doctorName(v.invite.doctor)} · {hm(v.invite.at)}</Tag>}>
        <Input.TextArea rows={5} value={note} onChange={(e) => setNote(e.target.value)} disabled={v.status === 'referral'}
          placeholder="Ghi theo Vấn đề → Giải pháp / phác đồ → Kết quả mong muốn → Điều khách cần cân nhắc. Không ghi chẩn đoán y khoa (thuộc hồ sơ bệnh án)." />
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <Hint>Nói với khách theo Vấn đề → Giải pháp → Kết quả. Không nêu tên máy, không hứa kết quả.</Hint>
          {!note.trim() && <Button size="small" type="link" className="!px-0" onClick={() => setNote(TEMPLATE)}>Chèn khung ghi</Button>}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2 border-t border-gray-100 pt-3 sm:flex sm:flex-wrap sm:items-center dark:border-gray-700">
          <Button onClick={save} disabled={!dirty}>Lưu ghi chép</Button>
          <Button icon={<Stethoscope size={14} />} onClick={() => setInvite(true)} disabled={v.status !== 'consulting'}>Mời bác sĩ khám</Button>
          <span className="hidden flex-1 sm:block" />
          <Tooltip title={!note.trim() ? 'Nên ghi chép sàng lọc trước khi mời bác sĩ' : undefined}>
            <Button type="primary" className="col-span-2 sm:col-span-1" icon={<Stethoscope size={14} />} onClick={() => go('/bac-si-kham')} disabled={v.status === 'referral' || v.status === 'waiting'}>Sang bước Bác sĩ khám</Button>
          </Tooltip>
          <Button className="col-span-2 sm:col-span-1" icon={<Flag size={14} />} onClick={() => go('/ket-qua-tu-van')} disabled={v.status === 'referral' || v.status === 'waiting'}>Ghi kết quả tư vấn</Button>
        </div>
      </Card>
      </div>

        {/* Tiến trình: khách đang ở bước nào (Telesales → check-in → các bước App Tư vấn), bên dưới là dòng thời gian chi tiết */}
        <Card
          className="lg:sticky lg:top-[72px]"
          title="Tiến trình của khách"
          extra={curIdx < 0
            ? <Tag color="green" className="!m-0">Đã xong các bước</Tag>
            : <Tag color="cyan" className="!m-0">Đang: {stages[curIdx].label}</Tag>}
        >
          <ol className="space-y-1">
            {stages.map((s, i) => {
              const cur = i === curIdx;
              return (
                <li key={s.key}>
                  <button
                    type="button"
                    disabled={!s.url}
                    onClick={() => s.url && go(s.url)}
                    className={`flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-[13px] ${cur ? 'bg-[#e8f4f7] ring-1 ring-[#3e91ab] dark:bg-[#3e91ab]/15' : s.url ? 'hover:bg-gray-50 dark:hover:bg-gray-700/40' : ''}`}
                  >
                    <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10.5px] font-bold ${s.done ? 'bg-emerald-500 text-white' : cur ? 'bg-[#3e91ab] text-white' : 'bg-gray-200 text-gray-500 dark:bg-gray-600 dark:text-gray-300'}`}>
                      {s.done ? <Check size={12} strokeWidth={3} /> : i + 1}
                    </span>
                    <span className={`min-w-0 flex-1 truncate ${cur ? 'font-semibold text-[#2c6b7f] dark:text-[#8cc8da]' : s.done ? 'text-gray-700 dark:text-gray-200' : 'text-gray-400'}`}>{s.label}</span>
                    <span className={`shrink-0 text-xs ${cur ? 'font-semibold text-[#2c6b7f] dark:text-[#8cc8da]' : 'text-gray-400'}`}>{cur ? 'Đang ở đây' : s.hint}</span>
                  </button>
                </li>
              );
            })}
          </ol>

          <div className="mb-2 mt-3 flex items-center justify-between gap-2 border-t border-gray-100 pt-3 text-xs font-semibold text-gray-600 dark:border-gray-700 dark:text-gray-300">
            <span>Chi tiết</span>
            {journey.length > 0 && (
              <span className="flex items-center gap-1.5">
                {changes > 0 && <Tag color="orange" className="!m-0">{changes} thay đổi</Tag>}
                <Button size="small" type="text" icon={showJourney ? <EyeOff size={13} /> : <Eye size={13} />} onClick={() => setStore({ showJourney: !showJourney })}>{showJourney ? 'Ẩn trước khi đến' : `Hiện trước khi đến (${journey.length})`}</Button>
              </span>
            )}
          </div>
          <div className="relative max-h-[360px] space-y-2.5 overflow-y-auto pl-4 before:absolute before:bottom-1 before:left-[5px] before:top-1 before:w-px before:bg-gray-200 dark:before:bg-gray-600">
            {showJourney && journey.map((j, i) => (
              <div key={`j${i}`} className="relative text-xs">
                <span className={`absolute -left-[14px] top-1 h-2 w-2 rounded-full ${j.change ? 'bg-amber-500' : 'bg-gray-300 dark:bg-gray-500'}`} />
                <span className="font-semibold text-gray-500">{dmhm(j.at)}</span> · <span className={j.change ? 'font-semibold text-amber-700 dark:text-amber-400' : 'text-gray-700 dark:text-gray-200'}>{j.text}</span> <span className="text-gray-400">— {j.by}</span>
              </div>
            ))}
            {showJourney && journey.length > 0 && v.log.length > 0 && (
              <div className="relative text-[11px] font-semibold uppercase tracking-wide text-[#2c6b7f] dark:text-[#8cc8da]">
                <span className="absolute -left-[15px] top-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-[#3e91ab] dark:border-gray-800" />
                Đến cơ sở · lượt này
              </div>
            )}
            {[...v.log].sort((a, b) => a.at - b.at).map((l, i) => (
              <div key={`l${i}`} className="relative text-xs">
                <span className="absolute -left-[14px] top-1 h-2 w-2 rounded-full bg-[#3e91ab]" />
                <span className="font-semibold text-gray-500">{dmhm(l.at)}</span> · <span className="text-gray-700 dark:text-gray-200">{l.text}</span> <span className="text-gray-400">— {l.by}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Modal open={invite} title="Mời bác sĩ khám" okText="Gửi lời mời" cancelText="Huỷ" onCancel={() => setInvite(false)} onOk={sendInvite}>
        <p className="mb-2 text-[13px] text-gray-500">Bác sĩ xem khai báo của khách, chỉ định dịch vụ và ký ở bước Bác sĩ khám (không thấy giá).</p>
        <Select value={doctor} onChange={setDoctor} className="w-full" options={doctors.map((d) => ({ value: d.id, label: d.name }))} />
      </Modal>
    </>
  );
}

export default function ConsultationTicketPage() {
  const { v } = useVisit();
  return (
    <Shell title="Phiếu tư vấn" sub="Hồ sơ khách, nhu cầu Telesales đã ghi, báo giá online và ghi chép tư vấn tại cơ sở">
      <FlowBar v={v} step="consult" />
      {v ? <DoctorGate v={v}><Body key={v.id} v={v} /></DoctorGate> : <NoVisit />}
    </Shell>
  );
}
